import React, { useState } from "react";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FactoryIcon from "@mui/icons-material/Factory";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RateReviewIcon from "@mui/icons-material/RateReview";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import {
  Card,
  Avatar,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import IconWithText from "../../../../../components/IconWithText";
import { formatDate } from "../../../../../helper/DateHelper";
import { capitalize } from "../../../../../helper/TextHelper";
import { MuiButton } from "../../../../../components";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import DownloadIcon from "@mui/icons-material/Download";
import ArticleIcon from "@mui/icons-material/Article";
import TopicIcon from "@mui/icons-material/Topic";
import LockIcon from "@mui/icons-material/Lock";
import { CASE_STUDY_TYPE_MINI } from "../../../helper/Constants";

export default function CaseStudyCard({
  refresh,
  caseStudy,
  handleEdit,
  handleView,
  handleDelete,
  handleTeachingNotes,
  createTeachingNote,
  handlePdf,
  handlePayment,
  handleAssignments,
  handleStandardCase,
  handleMiniCase,
}) {
  const [teachingNoteCreating, setTeachingNoteCreating] = useState(false);

  const handleTeachingNoteCreation = () => {
    setTeachingNoteCreating(true);
    createTeachingNote();
    setTimeout(() => {
      setTeachingNoteCreating(false);
      refresh();
    }, 15000);
  };

  const paymentRequired = caseStudy.paymentStatus === "PAYMENT_OPTION_REQUIRED";
  const isInReview = caseStudy.status === "IN_REVIEW";
  const isMiniCase = CASE_STUDY_TYPE_MINI === caseStudy.type;

  const avatar = (status) => {
    switch (status) {
      case "ACTIVE":
        return (
          <Avatar sx={{ bgcolor: "#008000" }}>
            <MenuBookIcon />
          </Avatar>
        );
      default:
        return <Avatar sx={{ bgcolor: "#9e9e9e" }}>{status.charAt(0)}</Avatar>;
    }
  };

  return (
    <Card elevation={1} sx={{ m: 0.5 }}>
      <CardHeader
        action={
          <Tooltip
            title={
              caseStudy.numberOfAssignments > 0
                ? "Cannot be deleted because it already has an associated project."
                : "Delete"
            }
          >
            <span>
              <IconButton
                color="secondary"
                onClick={handleDelete}
                disabled={caseStudy.numberOfAssignments > 0}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
        avatar={
          isInReview ? (
            <Avatar sx={{ bgcolor: "#ff9966" }}>
              <RateReviewIcon />
            </Avatar>
          ) : paymentRequired ? (
            <Avatar sx={{ bgcolor: "#f44336" }}>
              <LockIcon />
            </Avatar>
          ) : isMiniCase ? (
            <Avatar sx={{ bgcolor: "#B9D1A1" }}>
              <ImportContactsIcon />
            </Avatar>
          ) : (
            avatar(caseStudy.status)
          )
        }
        title={
          <Typography
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {caseStudy.title}
          </Typography>
        }
        subheader={"Created on: " + formatDate(caseStudy.createdAt)}
      />

      <CardContent>
        <IconWithText
          icon={<TopicIcon />}
          text={caseStudy.concept ? capitalize(caseStudy.concept) : "N/A"}
        />
        <IconWithText
          icon={<FactoryIcon />}
          text={capitalize(caseStudy.industry) + " Industry"}
        />
        <IconWithText
          icon={<StorefrontIcon />}
          text={caseStudy.marketType.toUpperCase() + " Market"}
        />
      </CardContent>

      <CardActions
        disableSpacing
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "4px",
          }}
        >
          {!isInReview && !paymentRequired && (
            <MuiButton
              size="small"
              text={
                "Share with students(" + caseStudy.numberOfAssignments + ")"
              }
              variant="text"
              onClick={handleAssignments}
            />
          )}
          {isInReview && (
            <MuiButton
              size="small"
              text={"Review and Finalize Case Study"}
              variant="text"
              onClick={isMiniCase ? handleMiniCase : handleStandardCase}
            />
          )}
        </div>
        <div style={{ marginRight: "16px" }}>
          {isInReview ? null : paymentRequired ? (
            <Tooltip title={"Unlock Case Study"}>
              <IconButton
                color={"warning"}
                onClick={handlePayment}
                size="large"
              >
                <PictureAsPdfIcon id="download-case-study" />
                <LockIcon
                  sx={{
                    fontSize: 20,
                    position: "absolute",
                    bottom: -1,
                    right: -2,
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={"Download Case Study"}>
              <IconButton color={"primary"} onClick={handlePdf} size="large">
                <PictureAsPdfIcon id="download-case-study" />
                <DownloadIcon
                  sx={{
                    fontSize: 20,
                    position: "absolute",
                    bottom: -1,
                    right: -2,
                  }}
                />
              </IconButton>
            </Tooltip>
          )}

          {isInReview || paymentRequired ? null : caseStudy.teachingNoteId ? (
            <Tooltip title={"Download Teaching Note"}>
              <IconButton
                color="primary"
                onClick={handleTeachingNotes}
                size="large"
              >
                <ArticleIcon />
                <DownloadIcon
                  sx={{
                    fontSize: 20,
                    position: "absolute",
                    bottom: -1,
                    right: -2,
                  }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            !isMiniCase && (
              <Tooltip title={"Create Teaching Note"}>
                <IconButton
                  disabled={teachingNoteCreating}
                  color="success"
                  onClick={() => handleTeachingNoteCreation()}
                  size="large"
                >
                  {teachingNoteCreating ? (
                    <CircularProgress size={24} color="success" />
                  ) : (
                    <NoteAddIcon id="create-teaching-note" />
                  )}
                </IconButton>
              </Tooltip>
            )
          )}
        </div>
      </CardActions>
    </Card>
  );
}
